import React, { useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';

const PDFJS_VERSION = '4.10.38';
const PDFJS_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.min.mjs`;
const PDF_WORKER_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.mjs`;

// Load pdf.js once and reuse it for every file.
let pdfjsPromise;
const loadPdfJs = () => {
  pdfjsPromise = import(/* webpackIgnore: true */ PDFJS_URL)
    .then((lib) => {
      lib.GlobalWorkerOptions.workerSrc = PDF_WORKER_URL;
      return lib;
    })
    .catch((err) => {
      pdfjsPromise = undefined; // allow retry after a failed load
      throw err;
    });
  return pdfjsPromise;
};

const normalizeItem = (item, index) => {
  const file = item instanceof Blob ? item : item?.file;
  return {
    file,
    name: item?.name || `Document ${index + 1}`,
    mimeType: item?.mime_type || file?.type || 'application/octet-stream',
  };
};

function useObjectUrl(blob) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!(blob instanceof Blob)) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);

  return url;
}

function PdfPages({ file }) {
  const containerRef = useRef(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const container = containerRef.current;
    let cancelled = false;
    let loadingTask;

    setError('');

    (async () => {
      try {
        const pdfjsLib = await loadPdfJs();
        const data = await file.arrayBuffer();
        if (cancelled) return;

        loadingTask = pdfjsLib.getDocument({ data });
        const pdf = await loadingTask.promise;

        for (let n = 1; n <= pdf.numPages; n++) {
          if (cancelled) return;

          const page = await pdf.getPage(n);
          if (cancelled) return;

          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.className = styles.pageCanvas;
          container.appendChild(canvas);

          await page.render({
            canvasContext: canvas.getContext('2d'),
            viewport,
          }).promise;
        }
      } catch (err) {
        if (!cancelled) {
          console.error('File rendering error:', err);
          setError('Unable to display this file.');
        }
      }
    })();

    return () => {
      cancelled = true;
      if (loadingTask?.destroy) {
        loadingTask.destroy();
      }
      if (container?.replaceChildren) {
        container.replaceChildren();
      }
    };
  }, [file]);

  if (error) return <div className={styles.error}>{error}</div>;
  return <div ref={containerRef} className={styles.pages} />;
}

function ImagePreview({ file, name }) {
  const url = useObjectUrl(file);
  if (!url) return null;

  return (
    <div className={styles.imageContainer}>
      <img src={url} alt={name} className={styles.image} />
    </div>
  );
}

function Unsupported({ file, name, mimeType }) {
  const url = useObjectUrl(file);

  const download = () => {
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.click();
  };

  return (
    <div className={styles.unsupported}>
      <div className={styles.fileIcon}>↧</div>
      <div className={styles.unsupportedName}>{name}</div>
      <div className={styles.mimeType}>{mimeType}</div>
      <button
        type="button"
        onClick={download}
        disabled={!url}
        className={styles.downloadButton}
      >
        Download file
      </button>
    </div>
  );
}

function Preview({ file, name, mimeType }) {
  if (!file) return <Unsupported file={file} name={name} mimeType={mimeType} />;
  if (mimeType === 'application/pdf') return <PdfPages file={file} />;
  if (mimeType.startsWith('image/')) return <ImagePreview file={file} name={name} />;
  return <Unsupported file={file} name={name} mimeType={mimeType} />;
}

function FileViewerSkeleton() {
  return (
    <div className={styles.wrapper} aria-busy="true" aria-live="polite">
      <div className={styles.toolbar}>
        <div className={`${styles.skeleton} ${styles.skeletonName}`} />
        <div className={`${styles.skeleton} ${styles.skeletonNav}`} />
      </div>

      <div className={styles.content}>
        <div className={`${styles.skeleton} ${styles.skeletonPage}`} />
      </div>
    </div>
  );
}

export default function FileViewer({ files = [], loading }) {
  const [requestedIndex, setRequestedIndex] = useState(0);

  if (loading) return <FileViewerSkeleton />;

  if (!files.length) {
    return <div className={styles.empty}>No files available.</div>;
  }

  // Derived, so no effect is needed to clamp the index when files shrink.
  const fileIndex = Math.min(requestedIndex, files.length - 1);
  const { file, name, mimeType } = normalizeItem(files[fileIndex], fileIndex);

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        <div className={styles.fileName} title={name}>
          {name}
        </div>

        <div className={styles.navigation}>
          <button
            type="button"
            onClick={() => setRequestedIndex(fileIndex - 1)}
            disabled={fileIndex === 0}
            className={styles.button}
            aria-label="Previous file"
          >
            ‹
          </button>

          <span className={styles.counter}>
            {fileIndex + 1} / {files.length}
          </span>

          <button
            type="button"
            onClick={() => setRequestedIndex(fileIndex + 1)}
            disabled={fileIndex === files.length - 1}
            className={styles.button}
            aria-label="Next file"
          >
            ›
          </button>
        </div>
      </div>

      <div className={styles.content}>
        <Preview key={fileIndex} file={file} name={name} mimeType={mimeType} />
      </div>
    </div>
  );
}